import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './lib/auth'
import { api } from './lib/api'
import { initSeoConfig, trackPageView } from './lib/seo'
import { trackRouteActivity } from './lib/activityTracker'

// Critical Entry Pages (Eagerly Loaded for Immediate First Paint)
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'

// Public Portal Pages (Lazy-Loaded)
const JobSearchPage = lazy(() => import('./pages/JobSearchPage').then(m => ({ default: m.JobSearchPage })))
const JobDetailPage = lazy(() => import('./pages/JobDetailPage').then(m => ({ default: m.JobDetailPage })))
const BrowseCategoriesPage = lazy(() => import('./pages/BrowseCategoriesPage').then(m => ({ default: m.BrowseCategoriesPage })))
const BrowseLocationsPage = lazy(() => import('./pages/BrowseLocationsPage').then(m => ({ default: m.BrowseLocationsPage })))
const CompaniesPage = lazy(() => import('./pages/CompaniesPage').then(m => ({ default: m.CompaniesPage })))
const CompanyProfilePage = lazy(() => import('./pages/CompanyProfilePage').then(m => ({ default: m.CompanyProfilePage })))
const BrowseSkillsPage = lazy(() => import('./pages/BrowseSkillsPage').then(m => ({ default: m.BrowseSkillsPage })))
const SalarySearchPage = lazy(() => import('./pages/SalarySearchPage').then(m => ({ default: m.SalarySearchPage })))
const CareerResourcesPage = lazy(() => import('./pages/CareerResourcesPage').then(m => ({ default: m.CareerResourcesPage })))
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage').then(m => ({ default: m.ArticleDetailPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const RazorpayTestPage = lazy(() => import('./pages/RazorpayTestPage').then(m => ({ default: m.RazorpayTestPage })))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const FresherHubPage = lazy(() => import('./pages/FresherHubPage').then(m => ({ default: m.FresherHubPage })))
const GovtJobsPage = lazy(() => import('./pages/GovtJobsPage').then(m => ({ default: m.GovtJobsPage })))
const GovtJobDetailPage = lazy(() => import('./pages/GovtJobDetailPage').then(m => ({ default: m.GovtJobDetailPage })))
const SafeHiringPage = lazy(() => import('./pages/SafeHiringPage').then(m => ({ default: m.SafeHiringPage })))
const SkillAssessmentPage = lazy(() => import('./pages/SkillAssessmentPage').then(m => ({ default: m.SkillAssessmentPage })))
const ReferEarnPage = lazy(() => import('./pages/ReferEarnPage').then(m => ({ default: m.ReferEarnPage })))

// Candidate Portal Pages (Lazy-Loaded)
const CandidateDashboardPage = lazy(() => import('./pages/CandidateDashboardPage').then(m => ({ default: m.CandidateDashboardPage })))
const CandidateProfilePage = lazy(() => import('./pages/CandidateProfilePage').then(m => ({ default: m.CandidateProfilePage })))
const CandidatePrivacyPage = lazy(() => import('./pages/CandidatePrivacyPage').then(m => ({ default: m.CandidatePrivacyPage })))
const JobAlertsPage = lazy(() => import('./pages/JobAlertsPage').then(m => ({ default: m.JobAlertsPage })))
const RecommendedJobsPage = lazy(() => import('./pages/RecommendedJobsPage').then(m => ({ default: m.RecommendedJobsPage })))
const SavedJobsPage = lazy(() => import('./pages/SavedJobsPage').then(m => ({ default: m.SavedJobsPage })))
const MyApplicationsPage = lazy(() => import('./pages/MyApplicationsPage').then(m => ({ default: m.MyApplicationsPage })))
const CvListPage = lazy(() => import('./pages/CvListPage').then(m => ({ default: m.CvListPage })))
const CvEditorPage = lazy(() => import('./pages/CvEditorPage').then(m => ({ default: m.CvEditorPage })))
const CvPreviewPage = lazy(() => import('./pages/CvPreviewPage').then(m => ({ default: m.CvPreviewPage })))
const MessagesPage = lazy(() => import('./pages/MessagesPage').then(m => ({ default: m.MessagesPage })))
const ContractsPage = lazy(() => import('./pages/ContractsPage').then(m => ({ default: m.ContractsPage })))

// Employer Portal Pages (Lazy-Loaded)
const EmployerLayout = lazy(() => import('./employer/EmployerLayout').then(m => ({ default: m.EmployerLayout })))
const EmployerDashboardPage = lazy(() => import('./employer/DashboardPage').then(m => ({ default: m.EmployerDashboardPage })))
const EmployerJobsPage = lazy(() => import('./employer/JobsPage').then(m => ({ default: m.EmployerJobsPage })))
const EmployerProfilePage = lazy(() => import('./employer/EmployerProfilePage').then(m => ({ default: m.EmployerProfilePage })))
const JobComposerPage = lazy(() => import('./employer/JobComposerPage').then(m => ({ default: m.JobComposerPage })))
const PipelineBoardPage = lazy(() => import('./employer/PipelineBoardPage').then(m => ({ default: m.PipelineBoardPage })))
const ApplicationsListPage = lazy(() => import('./employer/ApplicationsListPage').then(m => ({ default: m.ApplicationsListPage })))
const TasksPage = lazy(() => import('./employer/TasksPage').then(m => ({ default: m.TasksPage })))
const CandidateSearchPage = lazy(() => import('./employer/CandidateSearchPage').then(m => ({ default: m.CandidateSearchPage })))
const TalentPoolsPage = lazy(() => import('./employer/TalentPoolsPage').then(m => ({ default: m.TalentPoolsPage })))
const AnalyticsPage = lazy(() => import('./employer/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const TeamPage = lazy(() => import('./employer/TeamPage').then(m => ({ default: m.TeamPage })))
const BillingPage = lazy(() => import('./employer/BillingPage').then(m => ({ default: m.BillingPage })))
const InvoicesPage = lazy(() => import('./employer/InvoicesPage').then(m => ({ default: m.InvoicesPage })))

// Shared Layout & UI Components
import { HeaderNav } from './components/HeaderNav'
import { LoginModal } from './components/LoginModal'
import { MobileTabBar } from './components/MobileTabBar'
import { ToastContainer } from './components/Toast'

function PageLoadingFallback() {
  return (
    <div className="page-state" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span className="spinner-inline" style={{ width: '28px', height: '28px', borderWidth: '3px', marginRight: '10px' }} />
      <span>Loading…</span>
    </div>
  )
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <PageLoadingFallback />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />

  return children
}

export function App() {
  const location = useLocation()
  const isEmployerRoute = location.pathname.startsWith('/employer')
  const isJobDetailRoute = location.pathname.startsWith('/jobs/')

  // 1. Fetch public platform configuration & initialize SEO / Analytics keys
  useEffect(() => {
    api
      .config()
      .then((res) => {
        if (res.data?.settings) {
          initSeoConfig(res.data.settings as Record<string, any>)
        }
      })
      .catch(() => {
        // Fallback with defaults if network unavailable
        initSeoConfig({})
      })
  }, [])

  // 2. Track page views & client activity telemetry across route transitions
  useEffect(() => {
    trackPageView(location.pathname + location.search)
    trackRouteActivity(location.pathname + location.search)
  }, [location.pathname, location.search])

  return (
    <div className={`app ${isEmployerRoute ? 'app-fullscreen' : isJobDetailRoute ? 'app-wide' : ''}`}>
      <HeaderNav />
      <LoginModal />
      <MobileTabBar />
      <ToastContainer />

      <main className={isEmployerRoute ? 'main-fullscreen' : isJobDetailRoute ? 'main-wide' : ''}>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Public Portal */}
            <Route path="/" element={<HomePage />} />
            <Route path="/jobs" element={<JobSearchPage />} />
            <Route path="/jobs/:slug" element={<JobDetailPage />} />
            <Route path="/freshers" element={<FresherHubPage />} />
            <Route path="/govt-jobs" element={<GovtJobsPage />} />
            <Route path="/govt-jobs/:slug" element={<GovtJobDetailPage />} />
            <Route path="/safety" element={<SafeHiringPage />} />
            <Route path="/assessments" element={<SkillAssessmentPage />} />
            <Route path="/referrals" element={<ReferEarnPage />} />
            <Route path="/categories" element={<BrowseCategoriesPage />} />
            <Route path="/locations" element={<BrowseLocationsPage />} />
            <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/companies/:slug" element={<CompanyProfilePage />} />
            <Route path="/skills" element={<BrowseSkillsPage />} />
            <Route path="/salaries" element={<SalarySearchPage />} />
            <Route path="/resources" element={<CareerResourcesPage />} />
            <Route path="/resources/:slug" element={<ArticleDetailPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/test-payment" element={<RazorpayTestPage />} />
            <Route path="/razorpay-test" element={<RazorpayTestPage />} />

            {/* Candidate Portal */}
            <Route
              path="/candidate"
              element={
                <RequireAuth>
                  <CandidateDashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/profile"
              element={
                <RequireAuth>
                  <CandidateProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/privacy"
              element={
                <RequireAuth>
                  <CandidatePrivacyPage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/alerts"
              element={
                <RequireAuth>
                  <JobAlertsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/recommended"
              element={
                <RequireAuth>
                  <RecommendedJobsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/applications"
              element={
                <RequireAuth>
                  <MyApplicationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/applications"
              element={
                <RequireAuth>
                  <MyApplicationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/candidate/saved-jobs"
              element={
                <RequireAuth>
                  <SavedJobsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/contracts"
              element={
                <RequireAuth>
                  <ContractsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/cvs"
              element={
                <RequireAuth>
                  <CvListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/cvs/:id"
              element={
                <RequireAuth>
                  <CvEditorPage />
                </RequireAuth>
              }
            />
            <Route
              path="/cvs/:id/preview"
              element={<CvPreviewPage />}
            />
            <Route
              path="/messages"
              element={
                <RequireAuth>
                  <MessagesPage />
                </RequireAuth>
              }
            />

            {/* Company / Employer Portal */}
            <Route
              path="/employer"
              element={
                <RequireAuth>
                  <EmployerLayout />
                </RequireAuth>
              }
            >
              <Route index element={<EmployerDashboardPage />} />
              <Route path="jobs" element={<EmployerJobsPage />} />
              <Route path="jobs/new" element={<JobComposerPage />} />
              <Route path="profile" element={<EmployerProfilePage />} />
              <Route path="applications" element={<ApplicationsListPage />} />
              <Route path="pipeline" element={<PipelineBoardPage />} />
              <Route path="hiring" element={<PipelineBoardPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="candidates/search" element={<CandidateSearchPage />} />
              <Route path="talent-pools" element={<TalentPoolsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="billing/invoices" element={<InvoicesPage />} />
            </Route>

            <Route path="*" element={<div className="page-state">Page not found.</div>} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}